// Import secure client environment configuration
import { 
  CLIENT_CONFIG, 
  isProductionMode,
  initializeClientEnvironment 
} from './client-env-validator';
import { logger } from '@/lib/logger';

// NOTE: initializeClientEnvironment() is called from main.tsx line 24
// Do NOT call it here - creates circular dependency with logger

// Environment configuration management with security validation
export const ENV_CONFIG = {
  // Environment detection (validated)
  // CS-204: Vite never exposes NODE_ENV to the browser bundle, so this used to
  // be 'development' in every build. Fall back to Vite's own mode flag.
  NODE_ENV: import.meta.env.NODE_ENV || (isProductionMode() ? 'production' : 'development'),
  PROD: isProductionMode(),
  DEV: !isProductionMode(),
  
  // Application configuration (validated)
  AUTH_MODE: CLIENT_CONFIG.auth.mode,
  SESSION_TIMEOUT: CLIENT_CONFIG.auth.sessionTimeout,
  ENABLE_SECURITY_VALIDATION: CLIENT_CONFIG.development.enableSecurityValidation,
  
  
  // Supabase configuration (validated)
  SUPABASE_URL: CLIENT_CONFIG.supabase.url,
  SUPABASE_ANON_KEY: CLIENT_CONFIG.supabase.anonKey,
  
  // Stripe configuration (validated)
  STRIPE_PUBLISHABLE_KEY: CLIENT_CONFIG.stripe.publishableKey,
  
  // Feature flags
  FEATURES: {
    PAYMENTS_ENABLED: !!CLIENT_CONFIG.stripe.publishableKey,
    EMAIL_ENABLED: true, // Will be set via edge functions
    SUPERVISION_ENABLED: true,
    TECHNIQUE_ANALYSIS_ENABLED: true,
    COACH_PROGRESS_METRICS_ENABLED: !!CLIENT_CONFIG.features?.coachProgressMetrics,
  }
} as const;

// Environment helpers
// CS-204: derived from Vite's PROD/MODE (as client-env-validator does). The
// old NODE_ENV comparison was always false, so Sentry's beforeSend dropped
// every event in production.
export const isProduction = () => isProductionMode();
export const isStaging = () => ENV_CONFIG.NODE_ENV === 'staging';

// Authentication mode helpers - dev mode removed, always use real auth

// Configuration validation
export const validateConfig = () => {
  const errors: string[] = [];
  
  // Required in all environments
  if (!ENV_CONFIG.SUPABASE_URL) {
    errors.push('Missing VITE_SUPABASE_URL');
  }
  
  if (!ENV_CONFIG.SUPABASE_ANON_KEY) {
    errors.push('Missing VITE_SUPABASE_ANON_KEY');
  }
  
  // Required in production
  if (isProduction()) {
    if (ENV_CONFIG.AUTH_MODE !== 'real') {
      errors.push('AUTH_MODE must be "real" in production');
    }
    
    if (!ENV_CONFIG.STRIPE_PUBLISHABLE_KEY) {
      logger.warn('Stripe publishable key not configured', {
        context: 'config',
        action: 'missingStripeKey'
      });
    }
  }
  
  if (errors.length > 0) {
    throw new Error(`Configuration errors: ${errors.join(', ')}`);
  }
};

// Export config with validation
export const getValidatedConfig = () => {
  validateConfig();
  return ENV_CONFIG;
};

// Development utilities
export const getDebugInfo = () => {
  if (isProduction()) return null;
  
  return {
    environment: ENV_CONFIG.NODE_ENV,
    authMode: ENV_CONFIG.AUTH_MODE,
    features: ENV_CONFIG.FEATURES,
    hasSupabaseConfig: !!(ENV_CONFIG.SUPABASE_URL && ENV_CONFIG.SUPABASE_ANON_KEY),
    hasStripeConfig: !!ENV_CONFIG.STRIPE_PUBLISHABLE_KEY,
  };
};
